/**
 * AdminSenderRewritesPage - manage inbound From: rewrite rules.
 *
 * Use case: operator@fosdickcorp.com -> datacontrol@fosdickcorp.com.
 * When an inbound from the source address lands, the inbound pipeline
 * substitutes the target address before user resolution / threading /
 * ticket creation, so ticket attribution + outbound replies all go
 * to the target.
 *
 * Layout mirrors AdminSpamPage: add-form on top, table below, modal
 * confirm on delete. Rules are exact-match on from_email; to change
 * which source a rule covers, delete + recreate.
 */
import { useState, type FormEvent } from "react";

import { Alert, Button, Card, Input, Modal, Spinner } from "@/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useAddSenderRewrite,
  useAdminSenderRewrites,
  useDeleteSenderRewrite,
} from "@/hooks/useAdminSenderRewrites";
import type { SenderRewriteResponse } from "@/types/api";

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function AdminSenderRewritesPage(): JSX.Element {
  usePageTitle("Sender rewrites");

  const { data, isLoading, error } = useAdminSenderRewrites();
  const addRewrite = useAddSenderRewrite();
  const deleteRewrite = useDeleteSenderRewrite();

  const [fromEmail, setFromEmail] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [toName, setToName] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<
    SenderRewriteResponse | null
  >(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!fromEmail.trim() || !toEmail.trim()) {
      setFormError("Both from and to addresses are required.");
      return;
    }
    try {
      await addRewrite.mutateAsync({
        from_email: fromEmail.trim(),
        to_email: toEmail.trim(),
        to_name: toName.trim() ? toName.trim() : null,
        note: note.trim() ? note.trim() : null,
      });
      setFromEmail("");
      setToEmail("");
      setToName("");
      setNote("");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to add rewrite.",
      );
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemove) return;
    try {
      await deleteRewrite.mutateAsync(pendingRemove.id);
    } finally {
      setPendingRemove(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">
          Sender rewrites
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Rewrite an inbound email's From: address before any threading
          or ticket creation. Use this when a customer's technical
          mailbox sends mail, but every response should go to a
          different address (e.g. operator@fosdickcorp.com to
          datacontrol@fosdickcorp.com).
        </p>
      </header>

      <Card padding="md" className="mb-6">
        <h2 className="text-base font-semibold text-neutral-900">
          Add rewrite
        </h2>
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              id="rewrite-add-from"
              label="From (the inbound address)"
              value={fromEmail}
              placeholder="operator@fosdickcorp.com"
              onChange={(e) => setFromEmail(e.target.value)}
              required
              type="email"
            />
            <Input
              id="rewrite-add-to"
              label="To (rewritten address)"
              value={toEmail}
              placeholder="datacontrol@fosdickcorp.com"
              onChange={(e) => setToEmail(e.target.value)}
              required
              type="email"
            />
            <Input
              id="rewrite-add-name"
              label="Display name (optional)"
              value={toName}
              placeholder="Fosdick Data Control"
              onChange={(e) => setToName(e.target.value)}
            />
            <Input
              id="rewrite-add-note"
              label="Note (optional)"
              value={note}
              placeholder="Why this rule exists"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {formError && (
            <Alert variant="error" title="Could not add rewrite">
              {formError}
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              isLoading={addRewrite.isPending}
            >
              Add rewrite
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <Alert
          variant="error"
          title="Failed to load rewrites"
          className="mb-4"
        >
          {error instanceof Error ? error.message : "Unknown error."}
        </Alert>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  From
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  To
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Display name
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Note
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Created
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center">
                    <Spinner size="md" />
                  </td>
                </tr>
              )}
              {!isLoading && (data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-8 text-center text-sm text-neutral-500"
                  >
                    No rewrites configured yet.
                  </td>
                </tr>
              )}
              {(data ?? []).map((row) => (
                <tr key={row.id} className="text-sm">
                  <td className="px-3 py-2 font-mono text-neutral-800">
                    {row.from_email}
                  </td>
                  <td className="px-3 py-2 font-mono text-neutral-800">
                    {row.to_email}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {row.to_name ?? <span className="text-neutral-400">-</span>}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {row.note ?? <span className="text-neutral-400">-</span>}
                  </td>
                  <td className="px-3 py-2 text-neutral-500">
                    {formatTimestamp(row.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingRemove(row)}
                      disabled={deleteRewrite.isPending}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        title="Remove sender rewrite?"
      >
        <p className="text-sm text-neutral-700">
          Future messages from{" "}
          <span className="font-mono">{pendingRemove?.from_email}</span>{" "}
          will be attributed to the original sender again, not to{" "}
          <span className="font-mono">{pendingRemove?.to_email}</span>.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPendingRemove(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => void handleConfirmRemove()}
            isLoading={deleteRewrite.isPending}
          >
            Remove
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AdminSenderRewritesPage;
